import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axios } from '../hooks/useAuth';
import { useAuth } from '../hooks/useAuth';
import { useSocket } from '../hooks/useSocket';
import { Howl } from 'howler';
import '../styles/compilation.css';

function formatDuration(seconds) {
  if (!seconds) return '--:--';
  return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
}

export default function CompilationPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();

  const [compilation, setCompilation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [participants, setParticipants] = useState([]);

  const howlsRef = React.useRef({});

  useEffect(() => {
    axios.get(`/api/compilations/${id}`)
      .then(res => {
        setCompilation(res.data.compilation);
        axios.post(`/api/compilations/${id}/view`).catch(() => {});
      })
      .catch(() => setError('Compilation not found'))
      .finally(() => setLoading(false));
  }, [id]);

  // Socket: join compilation room for live updates
  useEffect(() => {
    if (!socket) return;
    socket.emit('compilation:join', { compilationId: id });

    socket.on('track:added', ({ track }) => {
      setCompilation(prev => ({
        ...prev,
        tracks: [...(prev.tracks || []), track],
        trackCount: (prev.trackCount || 0) + 1,
      }));
    });

    return () => {
      socket.emit('compilation:leave', { compilationId: id });
      socket.off('track:added');
    };
  }, [socket, id]);

  const handlePlayAll = () => {
    if (!compilation) return;
    const apiBase = process.env.REACT_APP_API_URL || '';

    if (isPlaying) {
      // Stop all
      Object.values(howlsRef.current).forEach(h => h.stop());
      howlsRef.current = {};
      setIsPlaying(false);
      return;
    }

    // Play original + all tracks simultaneously
    const urls = [
      `${apiBase}${compilation.song?.audioUrl || ''}`,
      ...compilation.tracks.map(t => `${apiBase}${t.audioUrl}`),
    ];

    urls.forEach((url, i) => {
      const howl = new Howl({
        src: [url],
        html5: true,
        volume: i === 0 ? 0.8 : 0.9,
        onend: () => { if (i === 0) setIsPlaying(false); },
      });
      // Apply sync offset for tracks
      if (i > 0 && compilation.tracks[i - 1]?.syncOffset) {
        // Delay or seek based on offset
        const offset = compilation.tracks[i - 1].syncOffset;
        if (offset > 0) {
          setTimeout(() => howl.play(), offset * 1000);
        } else {
          howl.seek(Math.abs(offset));
          howl.play();
        }
      } else {
        howl.play();
      }
      howlsRef.current[i] = howl;
    });
    setIsPlaying(true);
  };

  const handleDeleteTrack = async (trackId) => {
    if (!window.confirm('Delete this track?')) return;
    try {
      await axios.delete(`/api/compilations/${id}/tracks/${trackId}`);
      setCompilation(prev => ({
        ...prev,
        tracks: prev.tracks.filter(t => t.id !== trackId),
      }));
    } catch (err) {
      alert('Failed to delete track');
    }
  };

  if (loading) {
    return (
      <div className="page" style={{ textAlign: 'center', paddingTop: 80 }}>
        <div style={{ fontSize: 48 }} className="animate-float">🎵</div>
        <p className="text-muted">Loading compilation...</p>
      </div>
    );
  }

  if (error || !compilation) {
    return (
      <div className="page" style={{ textAlign: 'center', paddingTop: 80 }}>
        <p className="text-muted">{error || 'Not found'}</p>
        <button className="btn btn-primary mt-4" onClick={() => navigate('/songs')}>Browse Songs</button>
      </div>
    );
  }

  const isCreator = user?.id === compilation.creatorId;
  const canAddTrack = compilation.tracks?.length < compilation.maxTracks;

  return (
    <div className="compilation-page page-enter">
      {/* Header */}
      <div className="comp-header card">
        <div className="comp-song-info">
          {compilation.song?.coverArt ? (
            <img
              src={`${process.env.REACT_APP_API_URL || ''}${compilation.song.coverArt}`}
              alt=""
              className="comp-cover"
            />
          ) : (
            <div className="comp-cover-placeholder">🎵</div>
          )}
          <div className="comp-details">
            <div className="text-muted text-sm">Compilation</div>
            <h1 className="comp-title">{compilation.title}</h1>
            <p className="comp-song-name">
              {compilation.song?.title} — {compilation.song?.artist}
            </p>
            <div className="comp-stats flex gap-4 mt-2">
              <span className="text-sm text-muted">👁 {compilation.viewCount} views</span>
              <span className="text-sm text-muted">🎙️ {compilation.trackCount}/{compilation.maxTracks} tracks</span>
              <span className="text-sm text-muted">
                by {compilation.creator?.emoji} {compilation.creator?.displayName}
              </span>
            </div>
          </div>
        </div>

        <div className="comp-actions">
          <button
            className={`btn ${isPlaying ? 'btn-danger' : 'btn-primary'} btn-lg`}
            onClick={handlePlayAll}
          >
            {isPlaying ? '⏹ Stop' : '▶ Play All Tracks'}
          </button>

          {canAddTrack && (
            <button
              className="btn btn-secondary"
              onClick={() => navigate(`/sing/${compilation.songId}`)}
            >
              + Add Your Voice
            </button>
          )}
        </div>
      </div>

      {/* Tracks */}
      <div className="comp-tracks">
        <h2 style={{ marginBottom: 16 }}>🎙️ Vocal Tracks</h2>

        {compilation.tracks?.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 48 }}>🎙️</div>
            <h3 style={{ marginTop: 12 }}>No tracks yet</h3>
            <p className="text-muted">Be the first to add your voice!</p>
            <button
              className="btn btn-primary mt-4"
              onClick={() => navigate(`/sing/${compilation.songId}`)}
            >
              Add Your Voice
            </button>
          </div>
        ) : (
          <div className="tracks-list">
            {/* Original track */}
            <div className="track-item track-original">
              <div className="track-number">🎵</div>
              <div className="track-info">
                <div className="track-label">Original Backing Track</div>
                <div className="track-sub">{compilation.song?.title}</div>
              </div>
              <div className="track-meta">
                <span className="badge badge-gold">Original</span>
              </div>
              <audio
                controls
                src={`${process.env.REACT_APP_API_URL || ''}${compilation.song?.audioUrl}`}
                className="track-audio"
              />
            </div>

            {/* Vocal tracks */}
            {compilation.tracks.map((track, i) => (
              <div key={track.id} className="track-item animate-fade-in-up"
                style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="track-number">
                  {track.user?.emoji || '🎤'}
                </div>
                <div className="track-info">
                  <div className="track-label">{track.label || `Track ${track.trackNumber}`}</div>
                  <div className="track-sub">by {track.user?.displayName}</div>
                </div>
                <div className="track-meta">
                  {track.syncOffset !== 0 && (
                    <span className="text-sm text-muted font-mono">
                      {track.syncOffset > 0 ? '+' : ''}{track.syncOffset.toFixed(2)}s
                    </span>
                  )}
                </div>
                <audio
                  controls
                  src={`${process.env.REACT_APP_API_URL || ''}${track.audioUrl}`}
                  className="track-audio"
                />
                {(isCreator || user?.id === track.userId) && (
                  <button
                    className="btn btn-icon"
                    onClick={() => handleDeleteTrack(track.id)}
                    title="Delete track"
                    style={{ color: '#f87171' }}
                  >
                    🗑️
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {!canAddTrack && (
          <div className="alert alert-info mt-4">
            🎙️ This compilation has reached its maximum of {compilation.maxTracks} tracks.
          </div>
        )}
      </div>
    </div>
  );
}
